import * as React from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { toast } from "sonner";
import { Gavel } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input, Label, NativeSelect, Textarea } from "@/components/ui/input";
import { ActionDialog } from "@/components/action-dialog";
import { BackLink } from "@/components/back-link";
import { DataTable } from "@/components/data-table";
import {
  EmptyState,
  ErrorState,
  KeyValues,
  LoadingRows,
  Mono,
  PageHeader,
  StatusBadge,
} from "@/components/common";
import { api } from "@/lib/api";
import { ago, dateTime, minor, money, shortId, type Money } from "@/lib/format";
import { label } from "@/lib/labels";
import { parseEuroToMinor } from "@/lib/money-input";

interface DisputeRow {
  id: string;
  taskId: string;
  stage: string;
  reason: string;
  status: string;
  openedAt: string;
  invoiceTotal: Money | null;
}

const columns: ColumnDef<DisputeRow, unknown>[] = [
  { accessorKey: "reason", header: "Neden", cell: ({ row }) => label(row.original.reason) },
  {
    accessorKey: "stage",
    header: "Aşama",
    cell: ({ row }) =>
      row.original.stage === "before_charge" ? "Tahsilattan önce" : "Tahsilattan sonra",
  },
  {
    accessorKey: "status",
    header: "Durum",
    cell: ({ row }) => <StatusBadge status={row.original.status} />,
  },
  {
    accessorKey: "invoiceTotal",
    header: "Fatura",
    cell: ({ row }) => <span className="tabular">{money(row.original.invoiceTotal)}</span>,
  },
  { accessorKey: "openedAt", header: "Açıldı", cell: ({ row }) => ago(row.original.openedAt) },
];

export function DisputesPage() {
  const [params, setParams] = useSearchParams();
  const status = params.get("status") ?? "open";
  const navigate = useNavigate();
  const q = useQuery({
    queryKey: ["admin-disputes", status],
    queryFn: () => api<{ disputes: DisputeRow[] }>(`/v1/admin/disputes?status=${status}`),
  });
  return (
    <>
      <PageHeader
        title="İtirazlar"
        description="Müşterinin 72 saat içinde bildirdiği sorunlar (K-047). Ödeme karar verilene kadar bekletilir."
        actions={
          <NativeSelect
            aria-label="Durum"
            value={status}
            onChange={(e) => setParams({ status: e.target.value })}
          >
            <option value="open">Açık</option>
            <option value="in_review">Onay bekleyen</option>
            <option value="resolved">Çözülen</option>
          </NativeSelect>
        }
      />
      <DataTable
        columns={columns}
        data={q.data?.disputes}
        isLoading={q.isPending}
        error={q.error}
        onRetry={() => void q.refetch()}
        onRowClick={(d) => navigate(`/disputes/${d.id}`)}
        empty="Bu durumda itiraz yok."
      />
    </>
  );
}

interface DisputeDetail {
  dispute: Record<string, unknown> & {
    id: string;
    taskId: string;
    status: string;
    reason: string;
    stage: string;
    description: string | null;
    resolution: string | null;
    refundMinor: string | null;
    currency: string | null;
    openedAt: string;
    resolvedAt: string | null;
    evidencePaths: string[] | null;
  };
  media: { stage: string; mediaType: string; path: string; createdAt: string }[];
  messages: { senderId: string | null; body: string | null; createdAt: string }[];
}

function Resolve({ d, onDone }: { d: DisputeDetail["dispute"]; onDone: () => Promise<void> }) {
  const [resolution, setResolution] = React.useState("no_refund");
  const [amount, setAmount] = React.useState("");
  const [note, setNote] = React.useState("");
  const refundMinor = resolution === "partial_refund" ? parseEuroToMinor(amount) : null;
  const ok = note.trim().length >= 3 && (resolution !== "partial_refund" || refundMinor !== null);
  return (
    <ActionDialog
      trigger={
        <Button>
          <Gavel /> Karar ver
        </Button>
      }
      title="İtiraz kararı"
      description="Eşik üstü iade (€200) ikinci bir yöneticinin onayına gider (K-095). Not müşteriye ve hizmet verene gider."
      confirmLabel="Kararı kaydet"
      canSubmit={ok}
      success="Karar kaydedildi."
      onSubmit={async (key) => {
        const r = await api<{ status: string }>(`/v1/admin/disputes/${d.id}/resolve`, {
          method: "POST",
          idempotencyKey: key,
          body: { resolution, note: note.trim(), ...(refundMinor ? { refundMinor } : {}) },
        });
        if (r.status === "pending_approval")
          toast.info("Tutar eşiğin üstünde: ikinci onay bekleniyor.");
        await onDone();
      }}
    >
      <div className="grid gap-3">
        <div className="grid gap-2">
          <Label htmlFor="res">Karar</Label>
          <NativeSelect id="res" value={resolution} onChange={(e) => setResolution(e.target.value)}>
            <option value="no_refund">İade yok (iş yapılmış)</option>
            <option value="partial_refund">Kısmi iade</option>
            <option value="full_refund">Tam iade</option>
          </NativeSelect>
        </div>
        {resolution === "partial_refund" && (
          <div className="grid gap-2">
            <Label htmlFor="amt">İade tutarı (€)</Label>
            <Input
              id="amt"
              inputMode="decimal"
              placeholder="ör. 20,00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
            {amount && refundMinor === null && (
              <span className="text-xs text-destructive">Geçerli bir tutar girin.</span>
            )}
          </div>
        )}
        <div className="grid gap-2">
          <Label htmlFor="note">Gerekçe</Label>
          <Textarea
            id="note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={1000}
          />
        </div>
      </div>
    </ActionDialog>
  );
}

export function DisputeDetailPage() {
  const { id = "" } = useParams();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["admin-dispute", id],
    queryFn: () => api<DisputeDetail>(`/v1/admin/disputes/${id}`),
  });
  const back = <BackLink to="/disputes">İtirazlar</BackLink>;
  if (q.isPending)
    return (
      <>
        {back}
        <LoadingRows rows={6} />
      </>
    );
  if (q.error)
    return (
      <>
        {back}
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      </>
    );
  const { dispute: d, media, messages } = q.data;
  const onDone = async () => {
    await qc.invalidateQueries({ queryKey: ["admin-dispute", id] });
    await qc.invalidateQueries({ queryKey: ["admin-disputes"] });
  };
  return (
    <>
      <PageHeader
        back={back}
        title={
          <span className="flex items-center gap-3">
            {label(d.reason)} <StatusBadge status={d.status} />
          </span>
        }
        description={
          <span>
            İş{" "}
            <Link className="text-primary hover:underline" to={`/tasks/${d.taskId}`}>
              {shortId(d.taskId)}
            </Link>{" "}
            · <Mono>{d.id}</Mono>
          </span>
        }
        actions={d.status === "open" && <Resolve d={d} onDone={onDone} />}
      />
      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Müşterinin bildirimi</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4">
            <p className="whitespace-pre-line text-sm">{d.description ?? "—"}</p>
            <KeyValues
              items={[
                ["Aşama", d.stage === "before_charge" ? "Tahsilattan önce" : "Tahsilattan sonra"],
                ["Açıldı", dateTime(d.openedAt)],
                ["Kanıt", d.evidencePaths?.length ? `${d.evidencePaths.length} dosya` : "Yok"],
                ["Karar", d.resolution ? label(d.resolution) : "—"],
                ["İade", d.refundMinor ? minor(d.refundMinor, d.currency ?? "EUR") : "—"],
                ["Çözüldü", dateTime(d.resolvedAt)],
              ]}
            />
            <p className="text-xs text-muted-foreground">
              Fotoğraflar: işte {media.length} dosya. Görüntüleme ekranı sonraki adımda (imzalı
              bağlantı).
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Sohbet ({messages.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {messages.length ? (
              <ul className="grid max-h-96 gap-2 overflow-y-auto text-sm">
                {messages.map((m, i) => (
                  <li key={i} className="rounded-md bg-muted/60 px-3 py-2">
                    <div className="mb-0.5 flex justify-between text-xs text-muted-foreground">
                      <span>{m.senderId ? shortId(m.senderId) : "Sistem"}</span>
                      <span>{dateTime(m.createdAt)}</span>
                    </div>
                    <div className="whitespace-pre-line">
                      {m.body ?? <i className="text-muted-foreground">kaldırıldı</i>}
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <EmptyState>Mesaj yok.</EmptyState>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}

interface ApprovalRow {
  id: string;
  action_type: string;
  payload: Record<string, unknown>;
  requested_by: string;
  reason: string | null;
  status: string;
  created_at: string;
  expires_at: string | null;
}

export function ApprovalsPage() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["admin-approvals"],
    queryFn: () => api<{ approvals: ApprovalRow[] }>("/v1/admin/approvals"),
  });
  const columns: ColumnDef<ApprovalRow, unknown>[] = [
    {
      accessorKey: "action_type",
      header: "İşlem",
      cell: ({ row }) => label(row.original.action_type),
    },
    {
      id: "detail",
      header: "Ayrıntı",
      cell: ({ row }) => {
        const p = row.original.payload as {
          refundMinor?: string;
          disputeId?: string;
          resolution?: string;
          kind?: "member_role" | "role_permissions" | "super_admin";
          email?: string | null;
          roleName?: string;
          sensitivePermissions?: string[];
          add?: string[];
        };
        if (p.kind)
          return (
            <span className="text-sm">
              {p.kind === "super_admin" && (
                <>
                  <b>{p.email}</b> → super admin
                </>
              )}
              {p.kind === "member_role" && (
                <>
                  <b>{p.email}</b> → “{p.roleName}” rolü
                  <span className="block text-xs text-muted-foreground">
                    hassas: {p.sensitivePermissions?.join(", ")}
                  </span>
                </>
              )}
              {p.kind === "role_permissions" && (
                <>
                  “{p.roleName}” rolüne:{" "}
                  <span className="font-mono text-xs">{p.add?.join(", ")}</span>
                </>
              )}{" "}
              <Link className="text-primary hover:underline" to="/staff">
                ekip
              </Link>
            </span>
          );
        return (
          <span className="flex items-center gap-2">
            {p.refundMinor && <b className="tabular">{minor(p.refundMinor)}</b>}
            {p.resolution && label(p.resolution)}
            {p.disputeId && (
              <Link className="text-primary hover:underline" to={`/disputes/${p.disputeId}`}>
                itiraz
              </Link>
            )}
          </span>
        );
      },
    },
    {
      accessorKey: "reason",
      header: "Gerekçe",
      cell: ({ row }) => (
        <span className="line-clamp-2 max-w-sm">{row.original.reason ?? "—"}</span>
      ),
    },
    {
      accessorKey: "created_at",
      header: "İstendi",
      cell: ({ row }) => ago(row.original.created_at),
    },
    {
      accessorKey: "expires_at",
      header: "Son",
      cell: ({ row }) => dateTime(row.original.expires_at),
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }) => (
        <div className="flex justify-end gap-1">
          <ActionDialog
            trigger={
              <Button size="sm" variant="ghost">
                Reddet
              </Button>
            }
            title="Reddet"
            description="İşlem uygulanmaz. Kendi isteğinizi de buradan geri çekebilirsiniz."
            confirmLabel="Reddet"
            confirmVariant="destructive"
            success="Reddedildi."
            onSubmit={async (key) => {
              await api(`/v1/admin/approvals/${row.original.id}/reject`, {
                method: "POST",
                idempotencyKey: key,
                body: {},
              });
              await qc.invalidateQueries({ queryKey: ["admin-approvals"] });
              await qc.invalidateQueries({ queryKey: ["admin-staff"] });
            }}
          />
          <ActionDialog
            trigger={<Button size="sm">Onayla</Button>}
            title="İkinci onay"
            description="Kendi isteğinizi onaylayamazsınız. Onaylanınca işlem hemen uygulanır ve geri alınamaz."
            confirmLabel="Onayla ve uygula"
            success="Onaylandı ve uygulandı."
            onSubmit={async (key) => {
              await api(`/v1/admin/approvals/${row.original.id}/approve`, {
                method: "POST",
                idempotencyKey: key,
              });
              await qc.invalidateQueries({ queryKey: ["admin-approvals"] });
              await qc.invalidateQueries({ queryKey: ["admin-staff"] });
            }}
          />
        </div>
      ),
    },
  ];
  return (
    <>
      <PageHeader
        title="Dört göz onayı"
        description="Eşik üstü iadeler, hassas yetki ve super admin verme ikinci bir yöneticinin onayıyla uygulanır (K-095)."
      />
      <DataTable
        columns={columns}
        data={q.data?.approvals}
        isLoading={q.isPending}
        error={q.error}
        onRetry={() => void q.refetch()}
        empty="Onay bekleyen işlem yok."
      />
    </>
  );
}
